'use client';

/**
 * 🎨 범용 Asset 컴포넌트
 *
 * Asset CodeGen에 의해 자동 생성된 React 컴포넌트
 * 모든 Asset을 type-safe하게 사용할 수 있습니다
 */

import React, { forwardRef, useMemo } from 'react';
import { AssetProps, AssetName, AssetInfo, assetPathMap } from './types';
import { getAssetPath, getSizeStyle, getAssetColor, createCommonStyle, createErrorElement } from './utils';

/**
 * 범용 Asset 컴포넌트
 *
 * @example
 * // PNG 이미지 사용
 * <Asset type="icon" name="dance-race-belt-0-png" size="md" />
 *
 * // SVG 아이콘 사용
 * <Asset type="icon" name="dance-race-belt-0-svg" size="md" />
 *
 * // URL로 이미지 사용
 * <Asset type="url" src="/path/to/image.png" size={32} />
 *
 * // 커스텀 스타일
 * <Asset type="icon" name="dance-race-car-svg" size="lg" color="primary" className="my-icon" />
 *
 * // ref 사용
 * <Asset ref={myRef} type="icon" name="dance-race-car-png" size="lg" />
 */
export const Asset = forwardRef<HTMLImageElement, AssetProps>(function AssetComponent(props, ref) {
	const { size, color, className, style, 'aria-label': ariaLabel, alt, fallback, ratio, decorative } = props;

	// 사이즈 스타일 계산
	const sizeStyle = useMemo(() => getSizeStyle(size, ratio, style), [size, ratio, style]);

	// 색상 계산
	const actualColor = useMemo(() => getAssetColor(color), [color]);

	// width/height 속성 추출 (레이아웃 시프트 방지)
	const dimensionProps = useMemo(() => {
		const props: { width?: number; height?: number } = {};
		if (typeof sizeStyle.width === 'number') props.width = sizeStyle.width;
		if (typeof sizeStyle.height === 'number') props.height = sizeStyle.height;
		return props;
	}, [sizeStyle]);

	// 접근성 속성 계산
	const accessibilityProps = useMemo(() => {
		if (decorative) {
			return {
				alt: '',
				'aria-hidden': true as const,
				role: 'presentation' as const,
			};
		}
		return {
			alt: alt || ariaLabel || undefined,
			'aria-label': ariaLabel,
		};
	}, [decorative, alt, ariaLabel]);

	if (props.type === 'icon') {
		const { name } = props;
		const assetInfo = assetPathMap[name];

		if (!assetInfo) {
			console.warn(`Asset "${name}" not found in assetPathMap`);
			if (fallback) {
				return <>{fallback}</>;
			}
			return createErrorElement('not-found', sizeStyle, className, style, name);
		}

		const assetPath = getAssetPath(name);

		// 최종 스타일
		const commonStyle = createCommonStyle(sizeStyle, actualColor, style);

		// 기존 Asset 렌더링
		return (
			<img
				ref={ref}
				src={assetPath}
				{...accessibilityProps}
				className={`asset asset-${assetInfo.type} asset-${assetInfo.category} ${assetInfo.category && `asset-${assetInfo.category}`} ${className || ''}`}
				style={commonStyle}
				{...dimensionProps}
			/>
		);
	}

	if (props.type === 'url') {
		const { src } = props;

		// 최종 스타일
		const commonStyle = createCommonStyle(sizeStyle, actualColor, style);

		// URL 이미지 렌더링
		return (
			<img
				ref={ref}
				src={src}
				{...accessibilityProps}
				className={`asset asset-url ${className || ''}`}
				style={commonStyle}
				{...dimensionProps}
			/>
		);
	}

	return fallback || null;
});

Asset.displayName = 'Asset';

export default Asset;
